import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, IconButton } from '@chakra-ui/react';
import { Navigation } from 'swiper/modules';
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react';
import type { Swiper } from 'swiper';
import type { SwiperRef } from 'swiper/react';
import { SoftLink } from '../../components/soft-link/SoftLink';
import { useSupportsHover } from '../../hooks/useSupportsHover';
import {
    type ImageFetchPriorityType,
    type ImageLoadingType,
    ProductImage,
} from '../../pages/search/components/product-image';

interface SwiperMethods {
    slideTo: (index: number) => void;
    slideNext: () => void;
    slidePrev: () => void;
}

interface ProductImageCarouselProps {
    id: string;
    src: string;
    alt: string;
    loading: ImageLoadingType;
    fetchPriority: ImageFetchPriorityType;
}

interface ProductCarouselProps {
    id: string;
    images: ProductImageCarouselProps[];
    thumbnail: ProductImageCarouselProps;
    onImageChange?: (index: number) => void;
    className?: string;
    showNavigation?: boolean;
    showPagination?: boolean;
    autoPlay?: boolean;
    autoPlayInterval?: number;
    url: string;
}

export const ProductCarousel = ({
    id,
    images,
    thumbnail,
    onImageChange,
    className = '',
    showNavigation = true,
    showPagination = true,
    autoPlay = false,
    autoPlayInterval = 5000,
    url,
}: ProductCarouselProps): JSX.Element | null => {
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [lastHoveredIndex, setLastHoveredIndex] = useState<number>(0);
    const supportsHover = useSupportsHover();
    const swiperRef = useRef<SwiperRef | null>(null);
    const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
    const thumbnailHeightRef = useRef<string>('auto'); // Start with a reasonable default height
    const containerRef = useRef<HTMLDivElement>(null);
    const [thumbnailHeight, setThumbnailHeight] = useState<string>('auto');

    // Determine when to show the swiper carousel
    const showSwiper: boolean = isHovered || !supportsHover;

    const getSwiper = useCallback(
        (): SwiperMethods | undefined => swiperRef.current?.swiper as SwiperMethods | undefined,
        [],
    );

    const handleSlideChange = (swiper: Swiper): void => {
        const newIndex = (swiper as { realIndex: number }).realIndex;
        setCurrentIndex(newIndex);
        onImageChange?.(newIndex);
    };

    const goToSlide = useCallback(
        (index: number): void => {
            const swiper = getSwiper();
            swiper?.slideTo(index);
        },
        [getSwiper],
    );

    const goToNext = useCallback(() => {
        const swiper = getSwiper();
        swiper?.slideNext();
    }, [getSwiper]);

    const goToPrev = useCallback((): void => {
        const swiper = getSwiper();
        swiper?.slidePrev();
    }, [getSwiper]);

    // Pause auto-play on hover
    const handleMouseEnter = (): void => {
        setIsHovered(true);
        // Go to the last hovered position when hovering again
        if (lastHoveredIndex !== currentIndex) {
            goToSlide(lastHoveredIndex);
        }
    };

    const handleThumbnailLoad = (event: React.SyntheticEvent<HTMLImageElement>): void => {
        const img = event.currentTarget;
        const { offsetHeight: intrinsicHeight, naturalHeight, naturalWidth } = img;

        // Calculate height based on aspect ratio if offsetHeight is 0 (common on mobile/tablet)
        let heightToUse = intrinsicHeight;

        if (heightToUse === 0 && naturalHeight > 0 && naturalWidth > 0) {
            // Get container width to calculate proper height
            const containerWidth = containerRef.current?.offsetWidth || 500; // fallback to 500px
            // Calculate height based on aspect ratio: height = width * (naturalHeight / naturalWidth)
            heightToUse = containerWidth * (naturalHeight / naturalWidth);
        }

        // Only update height if we have a valid calculation and current height is 'auto'
        if (heightToUse > 0 && thumbnailHeight === 'auto') {
            const newHeight = `${heightToUse}px`;
            thumbnailHeightRef.current = newHeight;
            setThumbnailHeight(newHeight);
        }
    };

    const handleMouseLeave = (): void => {
        setIsHovered(false);
        // Remember the current position when blurring
        setLastHoveredIndex(currentIndex);
    };

    // Auto-play functionality
    useEffect(() => {
        if (autoPlay && !isHovered) {
            autoPlayRef.current = setInterval(() => {
                goToNext();
            }, autoPlayInterval);
        }

        return () => {
            if (autoPlayRef.current) {
                clearInterval(autoPlayRef.current);
            }
        };
    }, [autoPlay, isHovered, autoPlayInterval, goToNext]);

    // Keyboard navigation functionality
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (!showSwiper || images.length <= 1) return;

            switch (event.key) {
                case 'ArrowLeft':
                    event.preventDefault();
                    goToPrev();
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    goToNext();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showSwiper, images.length, goToPrev, goToNext]);

    // Reset currentIndex when thumbnail changes
    useEffect(() => {
        setCurrentIndex(0);
        setLastHoveredIndex(0);
    }, [thumbnail.id]);

    return (
        <Box
            ref={containerRef}
            id={`product-carousel-${id}`}
            data-testid={`product-carousel-${id}`}
            className={`relative w-full ${className}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Always render both elements, use display to hide/show */}
            <Box
                as="article"
                display={showSwiper ? 'none' : 'block'}
                w="full"
                style={{
                    height: thumbnailHeight,
                    overflow: 'hidden',
                }}
            >
                <SoftLink to={url} className="w-full">
                    <ProductImage
                        key={`product-thumbnail-${id}`}
                        id={`product-thumbnail-${id}`}
                        name={thumbnail.alt}
                        imageUrl={thumbnail.src}
                        loading={thumbnail.loading}
                        fetchPriority={thumbnail.fetchPriority}
                        onLoad={handleThumbnailLoad}
                    />
                </SoftLink>
            </Box>
            {/* Swiper requires at least 3 slides to loop properly.
                1-2 images: key="swiper-1-false" or key="swiper-2-false" (no loop)
                3+ images: key="swiper-3-true" or key="swiper-4-true" (with loop)
            */}
            <Box
                as="article"
                display={showSwiper ? 'block' : 'none'}
                w="full"
                style={{
                    height: thumbnailHeight,
                }}
            >
                <SwiperComponent
                    key={`swiper-${id}-${images.length}`}
                    ref={swiperRef}
                    slidesPerView={1}
                    spaceBetween={0}
                    loop={images.length > 2}
                    onSlideChange={handleSlideChange}
                    modules={[Navigation]}
                    pagination={false}
                    navigation={false}
                    allowTouchMove={images.length > 1}
                    className="w-full [&_.swiper-container]:!m-0 [&_.swiper-container]:!box-border [&_.swiper-container]:!w-full [&_.swiper-container]:!border-0 [&_.swiper-container]:!p-0 [&_.swiper-slide]:!w-full [&_.swiper-wrapper]:!w-full"
                    style={{
                        width: '100% !important',
                        height: thumbnailHeight,
                    }}
                >
                    {images.map(({ id: imageId, src, alt, loading, fetchPriority }, idx) => (
                        <SwiperSlide
                            key={`slide-img-${imageId}`}
                            className="!w-full"
                            style={{
                                width: '100% !important',
                                maxWidth: 'none !important',
                                minWidth: '0 !important',
                            }}
                        >
                            <SoftLink to={url} className="w-full">
                                <ProductImage
                                    id={`product-id-${imageId}-idx-${idx}`}
                                    name={alt}
                                    imageUrl={src}
                                    loading={loading}
                                    fetchPriority={fetchPriority}
                                />
                            </SoftLink>
                        </SwiperSlide>
                    ))}
                </SwiperComponent>
            </Box>

            {/* Custom Navigation Arrows - show when swiper is visible */}
            {showNavigation && images.length > 1 && isHovered && supportsHover && (
                <>
                    <IconButton
                        aria-label="Previous image"
                        icon={<ChevronLeftIcon />}
                        onClick={goToPrev}
                        position="absolute"
                        left={2}
                        top="50%"
                        transform="translateY(-50%)"
                        zIndex={30}
                        size="sm"
                        variant="outline"
                        colorScheme="gray"
                        bg="white"
                        borderColor="slt-pepper.300"
                        color="slt-pepper.1000"
                        borderRadius="full"
                        width={8}
                        height={8}
                        _hover={{
                            bg: 'gray.50',
                            borderColor: 'slt-pepper.400',
                        }}
                        _active={{
                            transform: 'translateY(-50%)',
                        }}
                        boxShadow="lg"
                        transition="all 0.2s"
                    />
                    <IconButton
                        aria-label="Next image"
                        icon={<ChevronRightIcon />}
                        onClick={goToNext}
                        position="absolute"
                        right={2}
                        top="50%"
                        transform="translateY(-50%)"
                        zIndex={30}
                        size="sm"
                        variant="outline"
                        colorScheme="gray"
                        bg="white"
                        borderColor="slt-pepper.300"
                        color="slt-pepper.1000"
                        borderRadius="full"
                        width={8}
                        height={8}
                        _hover={{
                            bg: 'gray.50',
                            borderColor: 'slt-pepper.400',
                        }}
                        _active={{
                            transform: 'translateY(-50%)',
                        }}
                        boxShadow="lg"
                        transition="all 0.2s"
                    />
                </>
            )}

            {/* Custom Pagination - show only on desktop, we keep the box here to avoid layout shifts */}
            <Box as="nav" className="relative flex h-[2px] w-full">
                {showPagination &&
                    images.length > 1 &&
                    showSwiper &&
                    images.map(({ id: imageId }, index) => (
                        <Box
                            key={`pagination-${imageId}`}
                            className={`cursor-pointer transition-all duration-200 ${
                                index === currentIndex ? 'bg-slt-pepper-1000' : 'bg-slt-pepper-200'
                            }`}
                            style={{
                                width: `calc(100% / ${images.length})`,
                            }}
                        />
                    ))}
            </Box>
        </Box>
    );
};
